import React, { useState, useReducer, useEffect } from "react";
import { ThemeProvider } from "styled-components";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import {
  themeFromSiteConfig,
  useSiteContext,
  useUserSettings
} from "@americanbible/uikit";

import {
  AppBarItem,
  AppBarSpacer,
  PickerContainer,
  PickerGroup,
  StyledButtonItem,
  SearchButtonItem,
  ParrallelButtonItem
} from "../../UI/Pickers";
import SearchIcon from "../../../icons/SearchIcon";
import CloseIcon from "../../../icons/CloseIcon";
import ParallelIcon from "../../../icons/LayoutThreeColumnsIcon";
import AppBar from "../../AppBar/AppBar";
import BibleDropdown from "./BibleDropdown/BibleDropdown";
import BookChapterDropdown from "./BookChapterDropdown/BookChapterDropdown";
import SearchInput from "../../SearchResults/SearchInput";
import { withBible } from "../../Bible/BibleContainer";

const MainPickerContainer = styled(PickerContainer)`
  flex: 1 0 20%;
  justify-content: left;
`;

const MainMenuPickerGroup = styled(PickerGroup)`
  z-index: 10;
  display: flex;
  flex-direction: row;
`;

const ParallelPickerGroup = styled(PickerGroup)`
  max-width: 100px;
`;

const ChapterBar = ({ platformType, bible, setModeDefault }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [openSubMenuIndex, setOpenSubMenuIndex] = useState(null);
  const site = useSiteContext();
  const { darkMode } = useUserSettings();

  /* start of temporary solution to make parallels button appear/disappear on orientationchage/resize */
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  useEffect(() => {
    const handleResize = () => window.addEventListener("resize", forceUpdate);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("orientationchange", handleResize);
      window.removeEventListener("resize", forceUpdate);
    };
  }, []);
  /* end of temporary solution to make parallels button appear/disappear on orientationchage/resize */

  const {
    onRemoveParallel,
    parallelBibleIds,
    onAddParallel,
    currentBibleId
  } = bible;
  const bibles = bible.getBibles();

  // Checking two values solves issue on iOS where window.innerWidth returns an invalid value when resizing
  // ref: https://bugs.webkit.org/show_bug.cgi?id=170595
  const windowWidth = Math.max(
    window.innerWidth,
    document.documentElement.clientWidth
  );
  const canHaveParallel = bibles && bibles.length > 1 && windowWidth >= 500;

  const toggleSubmenu = index => reset => {
    setModeDefault();
    setOpenSubMenuIndex(index === openSubMenuIndex || reset ? null : index);
  };

  const closeSubmenus = () => setOpenSubMenuIndex(null);
  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    setOpenSubMenuIndex(null);
  };
  const onParallelAddClick = () => {
    closeSubmenus();
    onAddParallel();
  };
  const onParallelRemoveClick = index => {
    closeSubmenus();
    onRemoveParallel(index);
  };

  return (
    <ThemeProvider theme={{ ...themeFromSiteConfig(site), darkMode }}>
      <AppBar showMenuButton>
        <AppBarSpacer />
        <MainPickerContainer key="main-menu">
          <MainMenuPickerGroup key={currentBibleId}>
            <SearchButtonItem onClick={toggleSearch}>
              <SearchIcon size={22} />
            </SearchButtonItem>

            {!searchOpen && bibles.length > 1 && (
              <AppBarItem key={`primary-${currentBibleId}`} id="bible-dropdown">
                <BibleDropdown
                  platformType={platformType}
                  isOpen={openSubMenuIndex === 0}
                  toggle={toggleSubmenu(0)}
                />
              </AppBarItem>
            )}

            {!searchOpen && (
              <AppBarItem>
                <BookChapterDropdown
                  isOpen={openSubMenuIndex === 1}
                  toggle={toggleSubmenu(1)}
                />
              </AppBarItem>
            )}

            {!searchOpen && canHaveParallel && (
              <ParrallelButtonItem onClick={onParallelAddClick}>
                <ParallelIcon size={22} />
              </ParrallelButtonItem>
            )}

            {searchOpen && [
              <SearchInput key="search" />,
              <StyledButtonItem onClick={toggleSearch} key="close-search">
                <CloseIcon size={16} />
              </StyledButtonItem>
            ]}
          </MainMenuPickerGroup>
        </MainPickerContainer>

        {// Parallel bible dropdowns
        !searchOpen &&
          parallelBibleIds.map((parallelId, index) => (
            <MainPickerContainer key={`${parallelId}-${index}`} grow={3}>
              <ParallelPickerGroup>
                <AppBarItem>
                  <BibleDropdown
                    isOpen={openSubMenuIndex === index + 2}
                    parallelIndex={index}
                    platformType={platformType}
                    toggle={toggleSubmenu(index + 2)}
                  />
                </AppBarItem>
                <StyledButtonItem onClick={() => onParallelRemoveClick(index)}>
                  <CloseIcon size={16} className="close-parallel" />
                </StyledButtonItem>
              </ParallelPickerGroup>
            </MainPickerContainer>
          ))}
        {// Do we still need this?
        parallelBibleIds.length > 0 && <AppBarSpacer />}
      </AppBar>
    </ThemeProvider>
  );
};

ChapterBar.propTypes = {
  bible: PropTypes.shape({
    getBibles: PropTypes.func.isRequired,
    currentBibleId: PropTypes.string,
    onRemoveParallel: PropTypes.func.isRequired,
    parallelBibleIds: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  platformType: PropTypes.string.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired
  }).isRequired,
  setModeDefault: PropTypes.func.isRequired
};

export default withRouter(withBible(ChapterBar));
